import { useApolloClient } from "@apollo/client";
import React, { useCallback } from "react";
import { Alert, Platform } from "react-native";

import WatchlistItemView from "./WatchlistItemView";

import { AnalyticsTracker } from "@/features/analytics";
import { CustomPromptRefProps } from "@/components/molecules/CustomPrompt/CustomPromptContainer";
import { useCurrentUser } from "@/features/auth";
import {
  useWatchlistDeleteWatchlistMutation,
  useWatchlistUpdateWatchlistMutation,
  WatchlistMyUserWatchlistFragment,
  WatchlistMyUserWatchlistFragmentDoc,
  WatchlistWatchlistMetaFragment,
} from "@/generated/graphql";
import useRecordUserGoal, { GoalType } from "@/hooks/useRecordUserGoal";
import { useTheme } from "@/providers/ThemeProvider";
import { ifWeb } from "@/utils/platform";

/**
 *
 */
interface Props {
  item: WatchlistWatchlistMetaFragment;
  canBeDeleted?: boolean;
  drag: () => void;
  isActive?: boolean;
  promptRef?: React.RefObject<CustomPromptRefProps>;
}

const showDragHandle = Platform.OS !== "web";

/**
 * Container component for watchlist item with edit and delete functionality
 * @param root0 - Component props
 * @param root0.item - Watchlist object to display and manage
 * @param root0.canBeDeleted - Whether the watchlist can be deleted (false for system watchlists)
 * @param root0.drag - Handler for drag-and-drop reordering
 * @param root0.isActive - Whether item is currently being dragged
 * @param root0.promptRef - Reference to prompt component for name input (mobile only)
 */
const WatchlistItemContainer = ({
  item,
  canBeDeleted,
  drag,
  isActive,
  promptRef,
}: Props) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const currentUser = useCurrentUser();
  const apolloClient = useApolloClient();
  const recordDeleteWatchlist = useRecordUserGoal({
    goalType: GoalType.DeleteWatchlist,
    value: item.id,
  });
  const recordUpdateWatchlist = useRecordUserGoal({
    goalType: GoalType.UpdateWatchlist,
    value: item.id,
  });
  const [deleteWatchlist] = useWatchlistDeleteWatchlistMutation({
    onCompleted: () => {
      recordDeleteWatchlist();
    },
    variables: { input: { id: item.id } },
    // No optimisticResponse: deleteWatchlist selects the full WatchlistWatchlist
    // player detail, but this row only holds the lightweight meta watchlist
    // (id-only players). An incomplete optimistic write floods the console with
    // Apollo "Missing field" (error 13) warnings (SE-5058). The update() below
    // removes the row from the list by id once the mutation resolves.
    update: (cache, { data, errors }) => {
      if (!errors) {
        const temporaryId = data?.deleteWatchlist.id;
        cache.modify({
          id: cache.identify({
            __typename: "Query",
            id: "ROOT_QUERY",
          }),
          fields: {
            listMyUserWatchlists(existingUserWatchlists = []) {
              return {
                ...existingUserWatchlists,
                edges: existingUserWatchlists.edges.filter(
                  (userWatchlistRef: { node: { __ref: string } }) => {
                    const userWatchlist: WatchlistMyUserWatchlistFragment | null =
                      cache.readFragment({
                        id: userWatchlistRef.node.__ref,
                        fragment: WatchlistMyUserWatchlistFragmentDoc,
                        fragmentName: "WatchlistMyUserWatchlist",
                      });
                    return userWatchlist?.watchlist.id !== temporaryId;
                  }
                ),
              };
            },
          },
        });
      }
    },
  });

  const [updateWatchList] = useWatchlistUpdateWatchlistMutation({
    onCompleted: () => {
      recordUpdateWatchlist();
      AnalyticsTracker.user.logWatchlistUpdated({
        user_id: currentUser?.id || "",
        organization_id: currentUser?.organizationId || "",
        phone_number: currentUser?.phoneNumber || "",
        watchlist_id: item.id,
        watchlist_name: item.name,
        action: "updated",
        platform: ifWeb("web", "mobile"),
        timestamp: new Date().toISOString(),
      });
    },
  });

  /**
   * Renames the watchlist with an instant optimistic update.
   *
   * We can't use the mutation's `optimisticResponse` here: WatchlistUpdateWatchlist
   * returns the full WatchlistWatchlist (with per-player season detail), but this
   * row only holds the lightweight meta watchlist (id-only players). A full-shaped
   * optimistic object built from the meta item floods Apollo with "Missing field"
   * (error 13) warnings (SE-5058). Instead we optimistically patch ONLY the `name`
   * field on the normalized Watchlist — shape-correct, instant, and with no effect
   * on the shared `players` connection — then let the mutation result reconcile.
   * On failure we roll the name back manually (optimisticResponse would have done
   * this automatically, but it isn't usable here).
   */
  const renameWatchlist = useCallback(
    (name: string) => {
      const previousName = item.name;
      const watchlistCacheId = apolloClient.cache.identify({
        __typename: "Watchlist",
        id: item.id,
      });
      apolloClient.cache.modify({
        id: watchlistCacheId,
        fields: { name: () => name },
      });
      updateWatchList({
        variables: { input: { id: item.id, name } },
      }).catch(() => {
        apolloClient.cache.modify({
          id: watchlistCacheId,
          fields: { name: () => previousName },
        });
      });
    },
    [apolloClient.cache, item.id, item.name, updateWatchList]
  );

  const handleEditPress = useCallback(() => {
    if (Platform.OS === "web") {
      const watchListName = window.prompt("Enter a name for this watchlist.");
      if (watchListName) {
        renameWatchlist(watchListName);
      }
    } else {
      promptRef?.current?.prompt(
        "Update Watchlist",
        "Enter a name for this watchlist.",
        name => renameWatchlist(name)
      );
    }
  }, [promptRef, renameWatchlist]);

  const recordDeleteWatchlistTracking = useCallback(() => {
    AnalyticsTracker.user.logWatchlistDeleted({
      user_id: currentUser?.id || "",
      organization_id: currentUser?.organizationId || "",
      phone_number: currentUser?.phoneNumber || "",
      watchlist_id: item.id,
      watchlist_name: item.name,
      action: "deleted",
      platform: ifWeb("web", "mobile"),
      timestamp: new Date().toISOString(),
    });
  }, [currentUser, item]);

  const handleRemovePress = useCallback(() => {
    if (!canBeDeleted) {
      return;
    }
    if (Platform.OS === "web") {
      const doDelete = window.confirm(
        `Are you sure you want to delete ${item.name}?`
      );
      if (doDelete) {
        deleteWatchlist();
        recordDeleteWatchlistTracking();
      }
    } else {
      Alert.alert(
        "Delete Watchlist",
        `Are you sure you want to delete ${item.name}?`,
        [
          {
            text: "Cancel",
            style: "cancel",
          },
          {
            text: "Yes",
            onPress: () => {
              deleteWatchlist();
              recordDeleteWatchlistTracking();
            },
            style: "destructive",
          },
        ]
      );
    }
  }, [canBeDeleted, deleteWatchlist, item.name, recordDeleteWatchlistTracking]);

  return (
    <WatchlistItemView
      onRemovePress={handleRemovePress}
      loading={false}
      item={item}
      canBeDeleted={canBeDeleted}
      onEditPress={handleEditPress}
      drag={drag}
      isActive={isActive}
      isDark={isDark}
      showDragHandle={showDragHandle}
    />
  );
};

export default WatchlistItemContainer;
